import React from 'react'
import { Menu, MenuItem, Typography } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrash } from '@fortawesome/pro-regular-svg-icons'
import { LabraToolTipV2 } from '@labrav/react-components'
import {
  useLabraChatContext,
  useLabraChatDispatch,
  useLabraChatSelector,
} from '../../../modules/labraChat/context'
import { deleteLabraChatSavedPrompt } from '../../../modules/labraChat/actions'
import { selectLabraChatSavedPrompts } from '../../../modules/labraChat/selectors'
import { useStyles } from './SavedPromptsMenu.styles'

interface SavedPromptsMenuProps {
  anchorEl: HTMLElement | null
  onClose: () => void
  /** Drops the picked prompt's text into the composer. Plain text, no template. */
  onSelect: (prompt: string) => void
}

export const SavedPromptsMenu: React.FC<SavedPromptsMenuProps> = ({
  anchorEl,
  onClose,
  onSelect,
}) => {
  const { classes } = useStyles()
  const { partnerId } = useLabraChatContext()
  const dispatch = useLabraChatDispatch()

  // Already in the store: the panel loads the list once when it opens, so this
  // renders instantly and never flashes a loading state.
  const prompts = useLabraChatSelector(selectLabraChatSavedPrompts)

  return (
    <Menu
      anchorEl={anchorEl}
      open={!!anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
      transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      slotProps={{
        paper: { className: classes.paper, elevation: 0 },
        list: { className: classes.list },
      }}
    >
      {prompts.length === 0 && (
        // TODO(copy): empty-state wording is not in Figma 345-2440 — confirm.
        <Typography className={classes.message}>
          No saved prompts yet.
        </Typography>
      )}
      {prompts.map(saved => (
        <MenuItem
          key={saved.id}
          className={classes.item}
          onClick={() => {
            onSelect(saved.prompt)
            onClose()
          }}
        >
          <LabraToolTipV2 tooltipPlacement="top" description={saved.prompt}>
            <Typography className={classes.itemText}>{saved.prompt}</Typography>
          </LabraToolTipV2>
          <button
            className={`${classes.deleteButton} savedPromptDelete`}
            type="button"
            aria-label={`Delete saved prompt: ${saved.prompt}`}
            onClick={event => {
              event.stopPropagation()
              dispatch(deleteLabraChatSavedPrompt(partnerId, saved.id))
            }}
          >
            <FontAwesomeIcon icon={faTrash} />
          </button>
        </MenuItem>
      ))}
    </Menu>
  )
}
